import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { getAnchorScrollAdjustment, getNextUnreadCount, isFollowingLatest } from "../domain/feed-position";
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
const FEED_ENTRY_SELECTOR = "[data-feed-entry]";
export function useFeedPosition({ resetKey, layoutKey, itemIds }) {
    const feedRef = useRef(null);
    const previousResetKey = useRef(resetKey);
    const previousItemIds = useRef(itemIds);
    const currentItemIds = useRef(itemIds);
    const previousTops = useRef(new Map());
    const readerAnchor = useRef(null);
    const followingLatest = useRef(true);
    const returningToLatest = useRef(false);
    const scrollEndTimer = useRef(undefined);
    const [isFollowingLatestState, setIsFollowingLatestState] = useState(true);
    const [newItemCount, setNewItemCount] = useState(0);
    currentItemIds.current = itemIds;
    useEffect(() => {
        const feed = feedRef.current;
        if (!feed) {
            return;
        }
        const syncReaderPosition = () => {
            const nextFollowingLatest = isFollowingLatest(getReaderScrollTop(feed));
            const changed = nextFollowingLatest !== followingLatest.current;
            followingLatest.current = nextFollowingLatest;
            readerAnchor.current = nextFollowingLatest
                ? null
                : captureReaderAnchor(feed);
            setIsFollowingLatestState((current) => current === nextFollowingLatest ? current : nextFollowingLatest);
            if (nextFollowingLatest) {
                setNewItemCount(0);
            }
            if (changed || nextFollowingLatest) {
                previousTops.current = measureEntryTops(feed);
            }
        };
        const finishLatestReturn = () => {
            window.clearTimeout(scrollEndTimer.current);
            scrollEndTimer.current = undefined;
            returningToLatest.current = false;
            syncReaderPosition();
        };
        const onScroll = () => {
            if (returningToLatest.current) {
                window.clearTimeout(scrollEndTimer.current);
                scrollEndTimer.current = window.setTimeout(finishLatestReturn, 140);
                if (isFollowingLatest(getReaderScrollTop(feed))) {
                    finishLatestReturn();
                }
                return;
            }
            syncReaderPosition();
        };
        const onViewportMove = () => {
            if (!returningToLatest.current) {
                syncReaderPosition();
            }
        };
        const resizeObserver = new ResizeObserver(onViewportMove);
        syncReaderPosition();
        feed.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("scroll", onViewportMove, { passive: true });
        window.addEventListener("resize", onViewportMove, { passive: true });
        resizeObserver.observe(feed);
        return () => {
            window.clearTimeout(scrollEndTimer.current);
            feed.removeEventListener("scroll", onScroll);
            window.removeEventListener("scroll", onViewportMove);
            window.removeEventListener("resize", onViewportMove);
            resizeObserver.disconnect();
        };
    }, []);
    useLayoutEffect(() => {
        const feed = feedRef.current;
        if (!feed) {
            return;
        }
        const resetChanged = previousResetKey.current !== resetKey;
        const followedLatestBeforeLayout = followingLatest.current;
        const anchorBeforeLayout = readerAnchor.current;
        const reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY).matches;
        const documentScrollMode = usesDocumentScrolling(feed);
        if (resetChanged) {
            returningToLatest.current = false;
            if (documentScrollMode) {
                window.scrollTo({ top: 0, behavior: "auto" });
            }
            else {
                feed.scrollTop = 0;
            }
            followingLatest.current = true;
            readerAnchor.current = null;
            setIsFollowingLatestState(true);
            setNewItemCount(0);
        }
        else if (!followedLatestBeforeLayout && anchorBeforeLayout) {
            preserveAnchor(feed, anchorBeforeLayout, documentScrollMode);
        }
        const nextTops = measureEntryTops(feed);
        if (!resetChanged &&
            followedLatestBeforeLayout &&
            !returningToLatest.current &&
            !reducedMotion) {
            animateRetainedEntries(feed, previousTops.current, nextTops);
        }
        if (!resetChanged) {
            setNewItemCount((currentUnreadCount) => getNextUnreadCount({
                currentUnreadCount,
                previousItemIds: previousItemIds.current,
                nextItemIds: currentItemIds.current,
                followingLatest: followedLatestBeforeLayout
            }));
        }
        const nextFollowingLatest = returningToLatest.current || isFollowingLatest(getReaderScrollTop(feed));
        followingLatest.current = nextFollowingLatest;
        readerAnchor.current = nextFollowingLatest
            ? null
            : captureReaderAnchor(feed);
        setIsFollowingLatestState((current) => current === nextFollowingLatest ? current : nextFollowingLatest);
        previousResetKey.current = resetKey;
        previousItemIds.current = [...currentItemIds.current];
        previousTops.current = nextTops;
    }, [layoutKey, newItemCount, resetKey]);
    const showLatest = useCallback(() => {
        const feed = feedRef.current;
        if (!feed) {
            return;
        }
        const reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY).matches;
        returningToLatest.current = !reducedMotion;
        followingLatest.current = true;
        readerAnchor.current = null;
        setIsFollowingLatestState(true);
        setNewItemCount(0);
        const scrollOptions = {
            top: 0,
            behavior: reducedMotion ? "auto" : "smooth"
        };
        if (usesDocumentScrolling(feed)) {
            window.scrollTo(scrollOptions);
        }
        else {
            feed.scrollTo(scrollOptions);
        }
    }, []);
    return {
        feedRef,
        isFollowingLatest: isFollowingLatestState,
        newItemCount,
        showLatest
    };
}
function captureReaderAnchor(feed) {
    const feedBounds = feed.getBoundingClientRect();
    const documentScrollMode = usesDocumentScrolling(feed);
    const viewportTop = documentScrollMode
        ? Math.max(feedBounds.top, getReaderViewportTop(feed))
        : feedBounds.top;
    const viewportBottom = documentScrollMode
        ? Math.min(feedBounds.bottom, window.innerHeight)
        : feedBounds.bottom;
    const entry = getFeedEntries(feed).find((candidate) => {
        const bounds = candidate.getBoundingClientRect();
        return bounds.bottom > viewportTop + 1 && bounds.top < viewportBottom;
    });
    const id = entry?.dataset.feedEntry;
    return entry && id
        ? { id, viewportTop: entry.getBoundingClientRect().top }
        : null;
}
function preserveAnchor(feed, anchor, documentScrollMode) {
    const entry = getFeedEntries(feed).find((candidate) => candidate.dataset.feedEntry === anchor.id);
    if (!entry) {
        return;
    }
    const adjustment = getAnchorScrollAdjustment(anchor.viewportTop, entry.getBoundingClientRect().top);
    if (Math.abs(adjustment) >= 0.5) {
        if (documentScrollMode) {
            window.scrollBy({ top: adjustment, behavior: "auto" });
        }
        else {
            feed.scrollTop += adjustment;
        }
    }
}
function usesDocumentScrolling(feed) {
    return window.getComputedStyle(feed).overflowY === "visible";
}
function getReaderScrollTop(feed) {
    if (!usesDocumentScrolling(feed)) {
        return feed.scrollTop;
    }
    return Math.max(0, getReaderViewportTop(feed) - feed.getBoundingClientRect().top);
}
function getReaderViewportTop(feed) {
    const gameHeader = feed.parentElement?.querySelector(".game-phone__header");
    const siteHeader = document.querySelector(".topbar");
    return Math.max(0, gameHeader?.getBoundingClientRect().bottom ?? 0, siteHeader?.getBoundingClientRect().bottom ?? 0);
}
function animateRetainedEntries(feed, previousTops, nextTops) {
    for (const entry of getFeedEntries(feed)) {
        const id = entry.dataset.feedEntry;
        if (!id) {
            continue;
        }
        const previousTop = previousTops.get(id);
        const nextTop = nextTops.get(id);
        if (previousTop === undefined || nextTop === undefined) {
            continue;
        }
        const delta = previousTop - nextTop;
        if (Math.abs(delta) < 1) {
            continue;
        }
        entry.animate([{ transform: `translateY(${delta}px)` }, { transform: "translateY(0)" }], {
            duration: 460,
            easing: "cubic-bezier(0.22, 1, 0.36, 1)"
        });
    }
}
function measureEntryTops(feed) {
    const tops = new Map();
    for (const entry of getFeedEntries(feed)) {
        const id = entry.dataset.feedEntry;
        if (id) {
            tops.set(id, entry.getBoundingClientRect().top);
        }
    }
    return tops;
}
function getFeedEntries(feed) {
    return Array.from(feed.querySelectorAll(FEED_ENTRY_SELECTOR)).filter((entry) => entry.dataset.feedEntry !== "incoming");
}
